import { useState } from 'react';
import type { ChangeEvent } from 'react';
import type { Comando, Pannello, ToneEff } from './data/pannelli.ts';

/** Colori delle conseguenze. Su fondo chiaro il verde e l'ambra vanno scuriti:
 *  i valori di riempimento (#1d6b48 / #e8b829) non passano 4,5:1 come testo. */
const COLORE: Record<ToneEff, string> = { ok: '#155c3c', att: '#7a5c00', ko: '#8f261a' };

export interface ComandoReso {
  id: string; nome: string; nota: string;
  esito: string; coloreEsito: string;
  acceso?: boolean;
  valore?: string | number;
  opzioni?: { valore: string; nome: string }[];
  min?: number; max?: number; passo?: number; etichettaValore?: string;
  avanzamento?: string; larghezza?: string;
  passi?: { nome: string; fatto: boolean; colore: string; onCambia: () => void }[];
  onCambia?: (ev: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  tipo: Comando['t'];
}

/**
 * Stato dei comandi di un pannello.
 *
 * DUE REGOLE:
 * 1. Il default vive SOLO nei dati (`c.def`): `valore()` lo legge da lì quando
 *    l'operatore non ha ancora toccato nulla. Copiare i default nello stato
 *    iniziale crea due verità che divergono al primo cambio dei dati.
 * 2. La conseguenza (`eff`) si valuta a OGNI render, non si memorizza: è una
 *    funzione del valore corrente, e memorizzarla la fa restare indietro.
 */
export function usePannello(pannello: Pannello | null): { controlli: ComandoReso[]; azzera: () => void } {
  const [ctrl, setCtrl] = useState<Record<string, boolean | string | number>>({});
  const [passiFatti, setPassiFatti] = useState<Record<string, string[]>>({});

  function valore(c: Comando): boolean | string | number | undefined {
    return ctrl[c.id] ?? (c as { def?: boolean | string | number }).def;
  }

  function imposta(id: string, v: boolean | string | number): void {
    setCtrl((p) => ({ ...p, [id]: v }));
  }

  function comando(c: Comando): ComandoReso {
    const base = { id: 'uc-' + c.id, nome: c.nome, nota: c.nota, tipo: c.t };

    if (c.t === 'passi') {
      const fatti = passiFatti[c.id] ?? [];
      const [esito, t] = c.eff(fatti.length, c.passi.length);
      return {
        ...base, esito, coloreEsito: COLORE[t],
        avanzamento: String(fatti.length) + ' di ' + String(c.passi.length),
        larghezza: String(Math.round((fatti.length / c.passi.length) * 100)) + '%',
        passi: c.passi.map(([pid, nome]) => ({
          nome,
          fatto: fatti.includes(pid),
          colore: fatti.includes(pid) ? COLORE.ok : '#3d4451',
          onCambia: () => { setPassiFatti((p) => {
            const cur = p[c.id] ?? [];
            return { ...p, [c.id]: cur.includes(pid) ? cur.filter((x) => x !== pid) : [...cur, pid] };
          }); },
        })),
      };
    }

    if (c.t === 'interruttore') {
      const v = valore(c) as boolean;
      const [esito, t] = c.eff(v);
      return { ...base, esito, coloreEsito: COLORE[t], acceso: v,
        onCambia: (ev) => { imposta(c.id, (ev.target as HTMLInputElement).checked); } };
    }

    if (c.t === 'scelta') {
      const v = valore(c) as string;
      const [esito, t] = c.eff(v);
      return { ...base, esito, coloreEsito: COLORE[t], valore: v,
        opzioni: c.opzioni.map(([valoreOpz, nome]) => ({ valore: valoreOpz, nome })),
        onCambia: (ev) => { imposta(c.id, ev.target.value); } };
    }

    const v = valore(c) as number;
    const [esito, t] = c.eff(v);
    return { ...base, esito, coloreEsito: COLORE[t], valore: v,
      min: c.min, max: c.max, passo: c.passo,
      // unita '' significa punteggio 0–1 mostrato come 0,90 (soglia delle skill)
      etichettaValore: c.unita === '%' ? String(v) + '%'
        : c.unita === '' ? String(v / 100).replace('.', ',')
        : String(v) + c.unita,
      onCambia: (ev) => { imposta(c.id, parseInt(ev.target.value, 10)); } };
  }

  return {
    controlli: pannello ? pannello.controlli.map(comando) : [],
    azzera: () => { setCtrl({}); setPassiFatti({}); },
  };
}

/**
 * La decisione motivata (oggi solo sui contrasti fra fonti).
 * La motivazione è OBBLIGATORIA: una scelta interpretativa senza il perché non
 * è utilizzabile in audit, quindi il pulsante non registra. Non è una
 * validazione di forma, è il requisito che rende la decisione difendibile.
 */
export function useDecisione(): {
  scelta: number | null;
  motivazione: string;
  registrata: boolean;
  valida: boolean;
  scegli: (i: number) => void;
  scrivi: (t: string) => void;
  registra: () => void;
  stato: string;
  coloreStato: string;
} {
  const [scelta, setScelta] = useState<number | null>(null);
  const [motivazione, setMotivazione] = useState('');
  const [registrata, setRegistrata] = useState(false);
  const valida = scelta !== null && motivazione.trim().length >= 12;

  return {
    scelta, motivazione, registrata, valida,
    scegli: (i: number) => { setScelta(i); setRegistrata(false); },
    scrivi: (t: string) => { setMotivazione(t); setRegistrata(false); },
    registra: () => { if (valida) setRegistrata(true); },
    stato: registrata
      ? 'Registrata. Compare nel fascicolo di ogni atto che applica questa soglia.'
      : scelta === null
        ? 'Scegli come si risolve il contrasto.'
        : !valida
          ? 'Serve una motivazione: la scelta senza il perché non è utilizzabile in audit.'
          : 'Pronta da registrare.',
    coloreStato: registrata ? COLORE.ok : scelta === null ? '#5d6472' : COLORE.att,
  };
}
